import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent
} from "react";
import { createPortal } from "react-dom";
import { CheckCircle2, Clock3, QrCode, X } from "lucide-react";
import type { Customer } from "./types";
import { registrationFormPresentation } from "./lib/registrationForm";

const QR_IMAGE_PATH = "/the-heal-hub-onboarding-qr.png";

export function RegistrationFormPanel({
  customer
}: {
  customer: Pick<Customer, "email" | "registrationFormSigned">;
}) {
  const presentation = registrationFormPresentation(customer);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrLoadFailed, setQrLoadFailed] = useState(false);
  const qrTriggerRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const StatusIcon = presentation.signed ? CheckCircle2 : Clock3;
  const closeQr = useCallback(() => {
    setQrOpen(false);
    qrTriggerRef.current?.focus();
  }, []);
  const handleDialogKeyboard = useCallback(
    (event: ReactKeyboardEvent<HTMLElement>) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        closeQr();
        return;
      }
      if (event.key !== "Tab") return;
      event.stopPropagation();

      const dialog = modalRef.current;
      const focusable = Array.from(
        dialog?.querySelectorAll<HTMLElement>(
          "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]"
        ) ?? []
      ).filter((element) => element.tabIndex >= 0 && !element.hasAttribute("hidden"));

      if (!dialog || focusable.length === 0) {
        event.preventDefault();
        closeButtonRef.current?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !dialog.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    },
    [closeQr]
  );

  useEffect(() => {
    if (qrOpen) closeButtonRef.current?.focus();
  }, [qrOpen]);

  return (
    <div className="detail-section registration-form-section">
      <section className={`registration-form-status-card ${presentation.tone}`}>
        <span className="registration-form-status-icon" aria-hidden="true">
          <StatusIcon size={25} />
        </span>
        <div>
          <span className="eyebrow">Kayıt formu durumu</span>
          <h3>{presentation.title}</h3>
          <p>{presentation.description}</p>
        </div>
        <span className="registration-form-seal" aria-hidden="true">
          {presentation.signed ? "İmzalı" : "Bekliyor"}
        </span>
      </section>

      <button
        ref={qrTriggerRef}
        className="button primary wide registration-form-qr-button"
        type="button"
        onClick={() => {
          setQrLoadFailed(false);
          setQrOpen(true);
        }}
      >
        <QrCode size={18} /> QR kodunu göster
      </button>

      {qrOpen && createPortal(
        <>
          <div
            className="registration-form-qr-scrim"
            aria-hidden="true"
            onClick={closeQr}
          />
          <section
            ref={modalRef}
            className="registration-form-qr-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="registration-form-qr-title"
            onKeyDown={handleDialogKeyboard}
          >
            <header>
              <div>
                <span className="eyebrow">The Heal Hub kayıt formu</span>
                <h2 id="registration-form-qr-title">QR kodunu okutun</h2>
              </div>
              <button
                ref={closeButtonRef}
                className="icon-button"
                type="button"
                aria-label="QR kodu penceresini kapat"
                onClick={closeQr}
              >
                <X size={20} />
              </button>
            </header>

            <div className={`registration-form-qr-frame ${qrLoadFailed ? "failed" : ""}`}>
              {qrLoadFailed ? (
                <div role="alert">
                  <QrCode size={34} />
                  <strong>QR kodu yüklenemedi.</strong>
                  <span>Sayfayı yenileyip yeniden deneyin.</span>
                </div>
              ) : (
                <img
                  src={QR_IMAGE_PATH}
                  alt="The Heal Hub kayıt formu QR kodu"
                  onError={() => setQrLoadFailed(true)}
                />
              )}
            </div>
            <p className="registration-form-qr-help">
              Müşteri telefonunun kamerasıyla kodu okutup kayıt formunu açabilir.
            </p>
          </section>
        </>,
        document.body
      )}
    </div>
  );
}
